// Hub for the six dedicated Palworld TCG chase-card valuation pages. Without
// this, /palworld-card-value (the parent path of the per-card pages, and a path
// people trim to by hand) returned a hard 404.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { CHASE_VALUE_CARDS } from "@/lib/chase-value-cards";
import { OFFICIAL_CARD_BY_CODE, SET_NAMES } from "@/lib/tcg-cards";
import { formatMoney } from "@/lib/prices-shared";
import { priceBoardQueryOptions } from "@/lib/price-board-query";
import { CardImage } from "@/components/CardImage";
import { RarityBadge } from "@/components/RarityBadge";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { ShareButton } from "@/components/ShareButton";
import { trackAffiliateClick } from "@/lib/affiliate-clicks";
import { goUrl } from "@/lib/outbound";
import { breadcrumbSchema, collectionSchema } from "@/lib/schema";

const SITE = "https://palstars.com";
const PATH = "/palworld-card-value";

export const Route = createFileRoute("/palworld-card-value/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(priceBoardQueryOptions()),
  head: () => {
    const title = "Palworld Card Value — Chase Card Valuations";
    const description =
      "Palworld TCG card value pages for the highest-priced English chase printings: live market price, price history graph, recorded daily prices and TCGplayer completed sales.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `${SITE}${PATH}` },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
      scripts: [
        breadcrumbSchema([
          ["Home", "/"],
          ["TCG Price Chart", "/prices"],
          ["Palworld Card Value", PATH],
        ]),
        collectionSchema({
          name: "Palworld Card Value — Chase Card Valuations",
          description: "Dedicated valuation pages for the highest-priced Palworld OFFICIAL CARD GAME chase printings.",
          path: PATH,
          items: CHASE_VALUE_CARDS.map((c) => {
            const card = OFFICIAL_CARD_BY_CODE.get(c.code.toUpperCase());
            return { name: `${card?.name ?? c.code} ${c.code} value`, path: `${PATH}/${c.code.toUpperCase()}` };
          }),
          numberOfItems: CHASE_VALUE_CARDS.length,
        }),
      ],
    };
  },
  component: CardValueIndex,
});

function CardValueIndex() {
  const { data } = useQuery(priceBoardQueryOptions());
  const cards = useMemo(
    () =>
      CHASE_VALUE_CARDS.map((entry) => {
        const code = entry.code.toUpperCase();
        const row = data?.rows.find((candidate) => candidate.code.toUpperCase() === code);
        const value = Math.max(row?.tcgMarket ?? 0, row?.ebayMarket ?? 0) || null;
        return { ...entry, card: OFFICIAL_CARD_BY_CODE.get(code), row, value };
      }).sort((a, b) => (b.value ?? -1) - (a.value ?? -1)),
    [data],
  );
  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-primary">Home</Link> ·{" "}
        <Link to="/prices" className="hover:text-primary">TCG Price Chart</Link> · <span>Palworld card value</span>
      </nav>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h1 className="text-3xl font-extrabold sm:text-4xl">Palworld Card Value</h1>
        <ShareButton
          title="Palworld Card Value — Pal Stars"
          text="Dedicated valuation pages for the highest-priced Palworld OFFICIAL CARD GAME chase printings."
          path="/palworld-card-value"
        />
      </div>
      <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
        Each chase printing below has its own valuation page with the live market (asking) price, a price history graph,
        a day-by-day record of every sync and the TCGplayer completed-sale average where a sale exists. eBay figures are
        active asking listings only.
      </p>

      <AffiliateDisclosure className="mt-4" updated={data?.updated} />

      <ul className="mt-6 grid gap-5 sm:grid-cols-2">
        {cards.map(({ code, angle, card, row, value }) =>
          card ? (
            <li key={code} className="glass rounded-2xl p-5">
              <Link to="/palworld-card-value/$code" params={{ code }} className="block hover:opacity-90">
                {card.image ? (
                  <CardImage
                    src={card.image}
                    alt={`${card.name} ${card.code} ${card.rarity} Palworld TCG card`}
                    width={320}
                    height={447}
                    responsive
                    sizes="(max-width: 640px) 65vw, 240px"
                    className="mx-auto w-48 rounded-lg sm:w-56"
                  />
                ) : null}
                <h2 className="mt-4 text-xl font-bold">
                  {card.name} {card.code} value
                </h2>
              </Link>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <RarityBadge code={card.code} />
                <span className="rounded-full border border-border/60 px-2 py-0.5">
                  {SET_NAMES[card.set] ?? card.set}
                </span>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{angle}</p>
              <div className="mt-4 border-t border-border/50 pt-4">
                <span className="text-xl font-bold tabular-nums">
                  {value === null ? "No current quote" : formatMoney(value, "USD")}
                </span>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span>TCGplayer {row?.tcgMarket ? formatMoney(row.tcgMarket, "USD") : "—"}</span>
                  <span>eBay {row?.ebayMarket ? formatMoney(row.ebayMarket, "USD") : "—"}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                <a
                  href={goUrl("tcgplayer", "card", code)}
                  target="_blank"
                  rel="nofollow ugc noopener noreferrer"
                  onClick={() => trackAffiliateClick({ source: "tcgplayer", kind: "card", targetKey: code })}
                  className="btn-tcgplayer rounded-md px-3 py-2 text-sm font-semibold"
                >
                  Buy on TCGplayer
                </a>
                <a
                  href={goUrl("ebay", "card", code)}
                  target="_blank"
                  rel="nofollow ugc noopener noreferrer"
                  onClick={() => trackAffiliateClick({ source: "ebay", kind: "card", targetKey: code })}
                  className="btn-ebay rounded-md px-3 py-2 text-sm font-semibold"
                >
                  Buy on eBay
                </a>
                </div>
              </div>
            </li>
          ) : null,
        )}
      </ul>

      <p className="mt-8 text-sm text-muted-foreground">
        Looking for a ranking instead?{" "}
        <Link to="/most-expensive-palworld-cards" className="text-primary hover:underline">
          Most expensive Palworld cards
        </Link>{" "}
        ·{" "}
        <Link to="/palworld-alternate-art-price-guide" className="text-primary hover:underline">
          Alternate art price guide
        </Link>
      </p>
    </main>
  );
}
